import React, { useCallback, useEffect, useRef, useState } from 'react'
import { connect, ConnectedProps, useSelector } from 'react-redux'
import { NavigationType, Outlet, useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom'

import { formatISO } from 'date-fns'
import { get, isEmpty, isNil } from 'lodash-es'
import { bindActionCreators } from 'redux'

import {
  assignmentHasCompletedResponse,
  assignmentHasProviderDraft,
  assignmentOnlyHasProviderDraft,
  getOpenActionPlansForEpisode,
} from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { useWindowVisible } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/useWindowVisible'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { noteSummaryStatusText } from '@lyrahealth-inc/shared-app-logic/src/models/appointmentSummary/AppointmentSummary'
import { Assignment } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { Episode, EpisodeStates } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import LoadingIndicator from '@lyrahealth-inc/ui-core/lib/atoms/loadingIndicator/LoadingIndicator'
import { BellIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/BellIcon'
import { useClientProfileHeaderCollapsed } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useClientProfileHeaderCollapsed'
import { useFetcher } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useFetcher'
import { FullScreenOverlay } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/fullScreenOverlay/FullScreenOverlay'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'

import styles from './client.module.scss'
import { setCurrentSessionCount } from './data/appointmentsSlice'
import { useClientAppointmentsData } from './data/hooks/useClientAppointmentsData'
import { useSelectedClientData } from './data/hooks/useSelectedClientData'
import { useLazyGetClientIdQuery } from './data/ltClientApi'
import { clearSelectedClientStore } from './data/ltClientDetailsAutoActions'
import { setSelectedClientId } from './data/ltClientSlice'
import { useSelectedEpisodeId } from './hooks/useSelectedEpisodeId'
import { useSyncClientRelId } from './hooks/useSyncClientRelId'
import { RedesignedClient } from './redesigned/RedesignedClient'
import {
  actions,
  actions as mixpanelActions,
  mixpanelEvents,
  sessionNoteTypes,
} from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import UpdateEpisodeModal from '../../../common/components/UpdateEpisode/UpdateEpisodeModal'
import {
  episodeStates,
  episodeTerminationStatuses,
  episodeTerminationStatusesEngageDA,
  programConfig as PROGRAM_CONFIG,
  ProgramConfig,
  SUPERVISOR_ROLES,
} from '../../../common/constants/appConstants'
import {
  CLIENT_END_OF_SESSION,
  CLIENT_NEW_NOTE,
  CLIENT_NOTES,
  CLIENT_RECORDS,
  CLIENTS_NEW_ASSIGNMENT,
  LT_CLIENTS,
} from '../../../common/constants/routingConstants'
import { hasRole, logToSentry } from '../../../common/utils/utils'
import { addAlert } from '../../../data/alertActions'
import { getAuthConfig, getAuthIsLTSupervisor, getAuthUser, getAuthUserId } from '../../../data/auth/authSelectors'
import {
  getClientCurrentSessionCount,
  getClientUnclosedPastAppointments,
  getSelectedClientId,
} from '../../../data/lyraTherapy/clientSelectors'
import { getLyraTherapyContentsData, getLyraTherapyContentsPrograms } from '../../../data/lyraTherapy/contentSelectors'
import { getRequestPaymentRates } from '../../../data/requestPayment/requestPaymentSelectors'
import { RootState, useAppDispatch } from '../../../data/store'
import { useIsEngageDAProvider } from '../../../hooks/useIsEngageDAProvider'
import { useNewMessageSound } from '../../../hooks/useNewMessageSound'
import { useVideoSessionBannerHeight } from '../../../hooks/useVideoSessionBannerHeight'
import { getProviderDetailsData } from '../../../providers/data/providerSelectors'
import { getProvider } from '../../../providers/individualProvider/data/providerDetailsActions'
import { getProviderRates } from '../../../requestPayment/data/requestPaymentActions'
import ActivityActionsModal from '../../assignments/ActivityActionsModal'
import { canManageAssignmentsForEpisode } from '../../assignments/data/assignmentAccess'
import { useAssignmentsData } from '../../assignments/data/hooks/useAssignmentsData'
import { useCompleteActionPlans } from '../../assignments/data/hooks/useCompleteActionPlans'
import ProviderExerciseDraft from '../../assignments/ProviderExerciseDraft'
import { getContentsByProgramId, getPrograms } from '../../data/ltContentsAutoActions'
import {
  getIsPrivacyGuardActive,
  getLTVideoSessionAppointmentId,
  getLTVideoSessionOpen,
} from '../../data/ltVideoSelectors'
import { useUpdateEpisodeMutation } from '../../episodes/data/episodesApi'
import { useEpisodesForSelectedClient } from '../../episodes/data/hooks/useEpisodesForSelectedClient'
import { useSelectedEpisodeForClient } from '../../episodes/data/hooks/useSelectedEpisodeForClient'
import { useConversationsData } from '../../messages/data/hooks/useConversationsData'
import { useSelectedConversation } from '../../messages/data/hooks/useSelectedConversation'
import {
  getConversationsClient,
  getMessagesActiveLiveMsgAppt,
  getMessagesLiveMsgClientIdToOpen,
  inLiveMsgSession,
} from '../../messages/data/messagesSelectors'
import {
  setLiveMsgClientIdToOpen,
  setSelectedConversation,
  toggleLiveMsgSession,
} from '../../messages/data/messagesSlice'

type ClientProps = ConnectedProps<typeof connector>
const Client: React.FC<ClientProps> = ({
  currentSessionCount,
  videoSessionOpen,
  appLocked,
  programs,
  inLiveMsgSession,
  liveMsgClientIdToOpen,
  activeLiveMsgAppt,
  rates,
  providerDetailsData,
  conversationsClient,
  pastAppointmentsToClose,
  contents,
  actions: { getContentsByProgramId, clearSelectedClientStore, getProviderRates, getPrograms, getProvider, addAlert },
}) => {
  const user = useSelector(getAuthUser)
  const config = useSelector(getAuthConfig)
  const [actionModalContent, setActionModalContent] = useState()
  const userId = useSelector(getAuthUserId)
  const [endEpisodeModalData, setEndEpisodeModalData] = useState<null | JSX.Element>(null)
  const [isUpdatingEpisode, setIsUpdatingEpisode] = useState(false)
  const [fullScreenOverlayContent, setFullScreenOverlayContent] = useState<React.ReactElement | null>(null)

  const [showSubNav, setShowSubNav] = useState(true)
  const clientId = useSelector(getSelectedClientId)
  const { data: clientDetails, isLoading: isLoadingClientDetails } = useSelectedClientData(undefined, {
    refetchOnMountOrArgChange: true,
  })
  const selectedConversation = useSelectedConversation()
  const [playSound] = useNewMessageSound()
  const windowVisible = useWindowVisible()
  const navigate = useNavigate()
  const navigationType = useNavigationType()
  const [forceMessagingHubToOpenPopover, setForceMessagingHubToOpenPopover] = useState(false)
  const [searchParams] = useSearchParams()
  const [selectedEpisodeId, episodeSelectHandler, lastProviderSpecificEpisode, isEpisodeSelectionResolved] =
    useSelectedEpisodeId()
  const { data: selectedEpisode, isLoading: isLoadingSelectedEpisode } = useSelectedEpisodeForClient(
    selectedEpisodeId,
  ) as {
    data: Episode | undefined
    isLoading: boolean
  }
  const videoSessionBannerHeight = useVideoSessionBannerHeight()

  // this is the React Router Dom way to detect users clicking the 'Back' browser button from the Close session screen and needing to show the subnav
  useEffect(() => {
    if (navigationType === NavigationType.Pop) {
      setShowSubNav(true)
    }
  }, [navigationType])

  const location = useLocation()
  const dispatch = useAppDispatch()
  const { enableExportClientRecords, isDAProviderMigrated } = useFlags()
  const isSupervisor = useSelector(getAuthIsLTSupervisor)
  const canExportClientRecords = enableExportClientRecords && isSupervisor
  const { data: conversations } = useConversationsData()
  const isEngageDAProvider = useIsEngageDAProvider()
  const { data: episodes, isLoading: loadingEpisodes } = useEpisodesForSelectedClient()
  const [updateEpisode] = useUpdateEpisodeMutation()
  const programConfig = selectedEpisode ? PROGRAM_CONFIG[selectedEpisode.program_name] : ({} as ProgramConfig)
  const {
    data: appointments,
    isLoading: loadingAppointments,
    isSuccess: fetchedAppointments,
  } = useClientAppointmentsData(undefined, { refetchOnMountOrArgChange: true })
  const videoSessionAppointmentId = useSelector(getLTVideoSessionAppointmentId)
  const [getClientId] = useLazyGetClientIdQuery()

  const appointmentToClose = pastAppointmentsToClose.find((appt: any) => {
    return appt.episodeId === selectedEpisode?.id
  })
  const canShowCloseSessionBanner =
    selectedEpisode?.program_name !== ProgramNames.DialecticalBehaviorTherapyTeens ||
    appointmentToClose?.lyraProviderId === userId

  useFetcher([getProvider, [user?.id], isEmpty(providerDetailsData), false])

  const editAssignment = (
    e: React.SyntheticEvent,
    _actionName: string,
    assignment: Assignment,
    sessionCount: number,
  ) => {
    e.preventDefault()
    navigate(CLIENTS_NEW_ASSIGNMENT.route, {
      state: {
        content: assignment,
        sessionCount: programConfig?.sessionLabel === 'Week' ? assignment.session_period : sessionCount,
        editMode: true,
      },
    })
  }

  const onExportClientRecords = () => {
    navigate(CLIENT_RECORDS.route)
  }

  const openActionModal = (e: any, actionName: any, assignment: Assignment, sessionCount: any) => {
    e.stopPropagation()
    track({
      event: 'BUTTON_PRESS',
      action: mixpanelActions.activities[actionName as keyof typeof mixpanelActions.activities],
    })
    // @ts-expect-error TS(2345): Argument of type '{ actionName: any; assignment: a... Remove this comment to see the full error message
    setActionModalContent({ actionName, assignment, sessionCount })
  }

  const startExerciseDraft = (e: any, actionName: string, assignment: Assignment, sessionCount: number) => {
    e.preventDefault()
    track({
      event: 'BUTTON_PRESS',
      action: mixpanelActions.activities[actionName as keyof typeof mixpanelActions.activities],
    })

    let metaData = {}
    let instructions = ''
    let contentId = ''
    if (!assignment.content_meta_data) {
      // If we are starting a draft for an Exercise in a future Session, it will not have any meta_data for
      // the form, so we need to get it from the store.
      const content = contents.find((content: any) => content.name === assignment.name)
      if (content) {
        metaData = content?.meta_data ?? {}
        instructions = content?.instructions ?? ''
        contentId = content?.id ?? ''
      }
    }

    setFullScreenOverlayContent(
      <ProviderExerciseDraft
        assignmentId={assignment.id}
        content={assignment.content_meta_data || metaData}
        contentId={assignment.content_id || contentId}
        instructions={assignment.instructions || instructions}
        name={assignment.content?.name || assignment.name}
        onExit={() => {
          setFullScreenOverlayContent(null)
        }}
        sessionCount={sessionCount}
        title={assignment.content?.title || assignment.title}
        selectedEpisodeId={selectedEpisodeId}
      />,
    )
  }

  const canManageEpisodeAssignments = canManageAssignmentsForEpisode(selectedEpisode, userId)

  const activityActions = [
    {
      name: 'edit',
      text: 'Edit',
      selectHandler: editAssignment,
      shouldShow: (assignment: Assignment, sessionCount: any) => {
        return (
          canManageEpisodeAssignments &&
          programConfig?.activityActions?.edit &&
          sessionCount >= (currentSessionCount ?? 0) &&
          (!['completed', 'in_progress'].includes(assignment.status) || assignmentOnlyHasProviderDraft(assignment))
        )
      },
    },
    {
      name: 'startDraft',
      text: 'Start a draft',
      selectHandler: startExerciseDraft,
      shouldShow: (assignment: Assignment) =>
        canManageEpisodeAssignments &&
        (assignment.content?.group === 'exercise' || assignment.group === 'exercise') &&
        !assignmentHasProviderDraft(assignment) &&
        !assignmentHasCompletedResponse(assignment),
    },
    {
      name: 'copy',
      text: 'Copy to...',
      selectHandler: openActionModal,
      shouldShow: () => canManageEpisodeAssignments && programConfig?.activityActions?.copy,
    },
    {
      name: 'move',
      text: 'Move to...',
      selectHandler: openActionModal,
      shouldShow: (_assignment: Assignment, sessionCount: any) =>
        canManageEpisodeAssignments &&
        programConfig?.activityActions?.move &&
        sessionCount >= (currentSessionCount ?? 0),
    },
    {
      name: 'unassign',
      text: 'Remove',
      selectHandler: openActionModal,
      shouldShow: (assignment: Assignment, sessionCount: number) => {
        return (
          canManageEpisodeAssignments &&
          programConfig?.activityActions?.unassign &&
          (!['completed', 'in_progress'].includes(assignment.status) || sessionCount > (currentSessionCount ?? 0))
        )
      },
    },
  ]

  const getClientFromQueryParams = useCallback(async () => {
    // Navigating away (e.g. Header provider switch) clears the client before this route unmounts
    const isLeavingRoute =
      `${window.location.pathname}${window.location.search}` !== `${location.pathname}${location.search}`
    if (!clientId && !isLeavingRoute) {
      const relationshipId = searchParams.get('relId')

      // redirect to client list if relId is missing or if user roles include supervisor roles (which means a provider hasn't been selected yet to emulate)
      if (relationshipId && !hasRole(user?.roles, SUPERVISOR_ROLES)) {
        const res = await getClientId({ relationshipId }).unwrap()
        const patientId = get(res, 'patient_id')
        const providerId = get(res, 'provider_id')
        if (patientId && providerId === user?.id) {
          dispatch(setSelectedClientId(patientId))
        } else {
          navigate(LT_CLIENTS.route)
        }
      } else {
        navigate(LT_CLIENTS.route)
      }
    }
  }, [
    clientId,
    location.pathname,
    location.search,
    searchParams,
    getClientId,
    user?.id,
    dispatch,
    navigate,
    user?.roles,
  ])

  const fetchedPrograms = useFetcher([[getPrograms, {}, isEmpty(programs)]], [(config as any).program])
  const areProgramsLoaded = fetchedPrograms[2] || !isEmpty(programs)
  // loadingAppts and fetchedAppts refer to loading/fetching Appointments AND Assignments
  const { data: assignments, isLoading: loadingAssignments } = useAssignmentsData(undefined, {
    refetchOnMountOrArgChange: true,
  })
  const completeActionPlans = useCompleteActionPlans()

  const [loadingRates] = useFetcher([getProviderRates, { id: user?.id }, isEmpty(rates)], [JSON.stringify(rates)])
  useFetcher(
    [[getContentsByProgramId, { programId: selectedEpisode?.program_id }]],
    [selectedEpisode?.program_id, user?.id],
  )

  const loading =
    loadingEpisodes ||
    loadingAssignments ||
    loadingAppointments ||
    loadingRates ||
    isLoadingClientDetails ||
    isLoadingSelectedEpisode

  const currentEpisodeId = selectedEpisode?.id

  useEffect(() => {
    if (appointments && currentEpisodeId) {
      dispatch(setCurrentSessionCount({ episodeId: currentEpisodeId, appointments }))
    }
  }, [appointments, currentEpisodeId, dispatch])

  useSyncClientRelId(clientId, episodes)

  useEffect(() => {
    getClientFromQueryParams()
  }, [getClientFromQueryParams])

  useEffect(
    () => () => {
      clearSelectedClientStore()
    },
    [clearSelectedClientStore],
  )

  // Default the selected conversation to the client's patient thread once per client. Re-asserting it on
  // every conversations cache change would clobber an explicit thread selection (e.g. the guardian thread
  // chosen in the messages popover) whenever a refetch or inbound Twilio message updates the cache.
  const defaultedConversationForClient = useRef<string | null>(null)
  useEffect(() => {
    if (defaultedConversationForClient.current === clientId) {
      return
    }
    const conversation = conversations?.find((conversation) => conversation.patient_lyra_id === clientId)
    if (conversation) {
      defaultedConversationForClient.current = clientId
      dispatch(setSelectedConversation(conversation.conversation_id))
    }
  }, [conversations, clientId, dispatch])

  // when a new message comes in
  // if the user is not in a video session and the window is not active, play a sound
  const onNewMessage = useCallback(
    (message) => {
      if (
        get(message, 'state.attributes.from') === user?.id ||
        message.conversation.sid !== selectedConversation?.conversation_id
      ) {
        return
      }
      if (!videoSessionOpen && !windowVisible) {
        playSound()
      }
    },
    [playSound, windowVisible, videoSessionOpen, user?.id, selectedConversation],
  )

  useEffect(() => {
    if (conversationsClient) {
      conversationsClient.on('messageAdded', onNewMessage)
    }
    return () => {
      if (conversationsClient) {
        conversationsClient.off('messageAdded', onNewMessage)
      }
    }
  }, [conversationsClient, onNewMessage])

  const getNotifications = () => {
    const notifications = []
    if (!isNil(appointmentToClose) && config?.showCloseSessionBanner && canShowCloseSessionBanner) {
      notifications.push({
        text: `Close Session ${appointmentToClose.sessionNumber}`,
        icon: BellIcon,
        clickAction: () => {
          track({
            event: mixpanelEvents.CLOSE_SESSION,
            details: {
              appointmentId: appointmentToClose.appointmentId,
              entryPoint: 'session',
            },
          })
          navigate(CLIENT_END_OF_SESSION.route)
        },
        isDisabled: videoSessionOpen && videoSessionAppointmentId === appointmentToClose.appointmentId,
        disabledTooltip: `Please hang up the video call before closing Session ${appointmentToClose.sessionNumber}`,
      })
    }

    return notifications
  }

  // Triggers if a user has joined a live message session via banner
  useEffect(() => {
    if (liveMsgClientIdToOpen && clientId === liveMsgClientIdToOpen) {
      if (!inLiveMsgSession && clientId === activeLiveMsgAppt?.userId) {
        dispatch(toggleLiveMsgSession(true))
      }
      setForceMessagingHubToOpenPopover(true)
      dispatch(setLiveMsgClientIdToOpen(null))
    }
  }, [liveMsgClientIdToOpen, clientId, dispatch, inLiveMsgSession, activeLiveMsgAppt])

  const {
    containerRef: clientProfileHeaderRef,
    isCollapsed: isClientProfileHeaderCollapsed,
    compensationHeight: clientProfileHeaderCompensationHeight,
  } = useClientProfileHeaderCollapsed()

  const outletContentContext = {
    activityActions,
    notifications: getNotifications(),
    appointment: appointmentToClose || {},
    setShowSubNav: setShowSubNav,
    clientDetailsSelectedEpisodeId: selectedEpisodeId || selectedEpisode?.id,
    isEpisodeSelectionResolved,
    areProgramsLoaded,
    lastProviderSpecificEpisode,
    setForceMessagingHubToOpenPopover,
    setClientDetailsSelectedEpisode: episodeSelectHandler,
  }
  const outletContent = <Outlet context={outletContentContext} />

  const { REFERRAL_NOTE, OLD_REFERRAL_NOTE } =
    Programs.getProgramConfigByName(selectedEpisode?.program_name)?.noteContentMetadataName ?? {}

  const referralNoteMetadata = contents.find((n: any) => n.name === REFERRAL_NOTE || n.name === OLD_REFERRAL_NOTE)
  const newReferralNote = {
    title: referralNoteMetadata?.title,
    content_meta_data: referralNoteMetadata,
    note_type: NoteTypes.REFERRAL,
  }

  const goToNewNote = (note: any) => {
    if ([NoteTypes.INITIAL, NoteTypes.SESSION].includes(note.note_type)) {
      track({
        event: mixpanelEvents.BUTTON_PRESS,
        action: actions.CLIENT_NOTES_TAB_OPEN_NOTE,
        details: {
          ...(note.aiSummaryStatus && {
            summaryStatus: noteSummaryStatusText[note.aiSummaryStatus as keyof typeof noteSummaryStatusText],
          }),
          noteType: sessionNoteTypes[note.note_type as keyof typeof sessionNoteTypes],
          ...(note.appointment_id && { appointmentId: note.appointment_id?.toString() }),
        },
      })
    }
    navigate(`${CLIENT_NEW_NOTE.route}`, { state: { note } })
  }

  const getTerminationStatuses = () => {
    const terminationStatuses = isDAProviderMigrated ? episodeTerminationStatusesEngageDA : episodeTerminationStatuses
    return episodes && episodes.length > 1 && currentSessionCount === 2
      ? terminationStatuses.map((terminationStatus) => {
          return { label: terminationStatus.text, value: terminationStatus.value }
        })
      : terminationStatuses
          .filter((status) => status.value !== 'booster')
          .map((terminationStatus) => {
            return { label: terminationStatus.text, value: terminationStatus.value }
          })
  }

  const modifyEpisode = (endEpisodeReason: string, comment: string) => {
    if (!selectedEpisode) {
      return
    }

    const currentEpisodeState = selectedEpisode?.state
    const updatedEpisodeFields: Partial<Episode> = {
      state: endEpisodeReason as EpisodeStates,
      end_episode_note: comment,
    }

    const clientName = clientDetails?.full_name || 'Client'
    let successMessage = `${clientName}'s episode has been updated.`
    const isEndingEpisode =
      currentEpisodeState === episodeStates.IN_PROGRESS && endEpisodeReason !== episodeStates.IN_PROGRESS

    if (isEndingEpisode) {
      successMessage = isEngageDAProvider
        ? `${clientName} has been successfully discharged.`
        : `${clientName}'s current episode has been successfully ended.`
      ;(updatedEpisodeFields as any).end_date = formatISO(new Date())
    } else if (currentEpisodeState !== endEpisodeReason) {
      successMessage = `${clientName}'s termination status has been updated.`
    }

    setIsUpdatingEpisode(true)
    updateEpisode({ ...selectedEpisode, ...updatedEpisodeFields })
      .unwrap()
      .then(() => {
        // Complete the Action Plan if there is one and the episode is ending.
        if (!isEndingEpisode) {
          return
        }
        return completeActionPlans(getOpenActionPlansForEpisode(assignments, selectedEpisode.id))
      })
      .then(() => {
        return addAlert({
          show: true,
          contents: successMessage,
          style: 'success',
          expires: true,
        })
      })
      .catch((error: any) => {
        logToSentry('endEpisode', { step: 'Error ending episode', clientId, errorMessage: error?.message }, 'error')
        return addAlert({
          show: true,
          contents: error?.response?.data?.message || 'There was an error ending the episode. Please try again.',
          style: 'danger',
          expires: true,
        })
      })
      .finally(() => {
        setIsUpdatingEpisode(false)
        setEndEpisodeModalData(null)
      })
  }

  const updateEpisodeModalContent = (
    <UpdateEpisodeModal
      selectedEpisode={selectedEpisode!}
      clientDetails={clientDetails!}
      closeModal={() => setEndEpisodeModalData(null)}
      updateEpisode={modifyEpisode}
      isUpdatingEpisode={isUpdatingEpisode}
      terminationStatuses={getTerminationStatuses()}
    />
  )

  const modalProps = {
    modalContents: endEpisodeModalData,
    onRequestClose: () => setEndEpisodeModalData(null),
    onCloseEnd: () => setEndEpisodeModalData(null),
  }

  let content = outletContent

  const isNotes = location.pathname.startsWith(CLIENT_NOTES.route)
  if (clientDetails) {
    content = (
      <RedesignedClient
        content={outletContent}
        userId={userId}
        canReferCare={currentEpisodeId === selectedEpisode?.id && !!referralNoteMetadata}
        isEndEpisodeButtonVisible={
          selectedEpisode?.state === episodeStates.IN_PROGRESS && userId === selectedEpisode?.provider_id
        }
        canEndEpisode={fetchedAppointments && isEmpty(appointmentToClose)}
        goToNewNote={() => goToNewNote(newReferralNote)}
        endEpisode={() => {
          setEndEpisodeModalData(updateEpisodeModalContent)
        }}
        modalProps={modalProps}
        clientDetails={clientDetails}
        setClientDetailsSelectedEpisode={episodeSelectHandler}
        selectedEpisodeId={selectedEpisodeId}
        currentEpisode={selectedEpisode}
        setForceMessagingHubToOpenPopover={setForceMessagingHubToOpenPopover}
        forceMessagingHubToOpenPopover={forceMessagingHubToOpenPopover}
        contentHasNoMargin={isNotes}
        showSubNav={showSubNav}
        appLocked={appLocked}
        onExportClientRecords={canExportClientRecords ? onExportClientRecords : undefined}
        clientProfileHeaderRef={clientProfileHeaderRef}
        isClientProfileHeaderCollapsed={isClientProfileHeaderCollapsed}
        clientProfileHeaderCompensationHeight={clientProfileHeaderCompensationHeight}
      />
    )
  }

  if (loading) {
    return (
      <div className={styles['loading-container']}>
        <LoadingIndicator size={45} />
      </div>
    )
  }

  return (
    <div>
      <Modal
        visible={Boolean(actionModalContent)}
        modalContents={
          <ActivityActionsModal
            data={actionModalContent}
            currentSession={currentSessionCount}
            closeModal={() => setActionModalContent(undefined)}
            selectedEpisodeId={selectedEpisodeId}
          />
        }
        onRequestClose={() => setActionModalContent(undefined)}
      />
      <FullScreenOverlay
        isOpen={!!fullScreenOverlayContent}
        customStyles={videoSessionOpen ? { top: `${videoSessionBannerHeight}px`, zIndex: '10' } : {}}
        isFocused={false}
      >
        {fullScreenOverlayContent}
      </FullScreenOverlay>
      {content}
    </div>
  )
}
const mapStateToProps = (state: RootState) => {
  return {
    currentSessionCount: getClientCurrentSessionCount(state),
    videoSessionOpen: getLTVideoSessionOpen(state),
    appLocked: getIsPrivacyGuardActive(state),
    // Appointments are ordered by appointment start date, hence, we do a find to get
    // the first appointment that hasn't been completed.
    // An appointment needs to be closed out if its start date is in the past, and its status
    // is either 'rescheduled', 'newAppt' or 'concluded' ('concluded' meaning that the appointment happened)
    programs: getLyraTherapyContentsPrograms(state),
    pastAppointmentsToClose: getClientUnclosedPastAppointments(state) ?? [],

    inLiveMsgSession: inLiveMsgSession(state),
    activeLiveMsgAppt: getMessagesActiveLiveMsgAppt(state),
    liveMsgClientIdToOpen: getMessagesLiveMsgClientIdToOpen(state),
    rates: getRequestPaymentRates(state),
    providerDetailsData: getProviderDetailsData(state),
    conversationsClient: getConversationsClient(state),
    contents: getLyraTherapyContentsData(state),
  }
}

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      clearSelectedClientStore,
      getProviderRates,
      getContentsByProgramId,
      getPrograms,
      getProvider,
      addAlert,
    },
    dispatch,
  ),
})
const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(Client)
